import {
  acceptInvite,
  getUser,
  handleAuthCallback,
  login,
  logout,
  refreshSession,
  requestPasswordRecovery,
  updateUser,
} from '@netlify/identity';

export interface Account {
  readonly email: string;
}

/** What a link in a sign-in email asked for, once its token has been read from the URL. */
export type Callback =
  | { readonly kind: 'invite'; readonly token: string }
  | { readonly kind: 'recovery' }
  | { readonly kind: 'signedIn' };

/** Sign-in, behind an interface, so the UI can be tested without Netlify Identity. */
export interface AuthService {
  current(): Promise<Account | null>;
  signIn(email: string, password: string): Promise<Account>;
  signOut(): Promise<void>;
  requestReset(email: string): Promise<void>;
  /** Reads a confirmation, invite or recovery token from the URL, if the page has one. */
  callback(): Promise<Callback | null>;
  acceptInvite(token: string, password: string): Promise<Account>;
  setPassword(password: string): Promise<void>;
  /**
   * Makes the sign-in token current before a request; it reads the token's expiry locally
   * and reaches the network only when the token has expired or is about to.
   */
  renew(): Promise<void>;
}

const AUTH_HASH =
  /^#(confirmation_token|recovery_token|invite_token|email_change_token|access_token)=/;

const accountOf = (user: { email?: string | undefined } | null): Account | null =>
  user?.email ? { email: user.email } : null;

function signedIn(user: { email?: string | undefined } | null): Account {
  const account = accountOf(user);
  if (!account) throw new Error('Signed in, but Netlify Identity returned no email');
  return account;
}

/**
 * Netlify Identity, through `@netlify/identity`. It keeps the session in the `nf_jwt` cookie,
 * which the drawings API reads, so requests need no token of their own.
 */
export class IdentityAuth implements AuthService {
  /** One renewal at a time, shared by every request that asks while it runs. */
  private renewing: Promise<void> | null = null;

  async current(): Promise<Account | null> {
    return accountOf(await getUser());
  }

  async signIn(email: string, password: string): Promise<Account> {
    return signedIn(await login(email, password));
  }

  async signOut(): Promise<void> {
    await logout();
  }

  async requestReset(email: string): Promise<void> {
    await requestPasswordRecovery(email);
  }

  async callback(): Promise<Callback | null> {
    if (typeof location === 'undefined' || !AUTH_HASH.test(location.hash)) return null;
    const result = await handleAuthCallback();
    // The token is spent; take it out of the address bar so a reload does not retry it.
    history.replaceState(history.state, '', location.pathname + location.search);
    if (!result) return null;
    if (result.type === 'invite' && result.token) return { kind: 'invite', token: result.token };
    if (result.type === 'recovery') return { kind: 'recovery' };
    return { kind: 'signedIn' };
  }

  async acceptInvite(token: string, password: string): Promise<Account> {
    return signedIn(await acceptInvite(token, password));
  }

  async setPassword(password: string): Promise<void> {
    await updateUser({ password });
  }

  renew(): Promise<void> {
    this.renewing ??= refreshSession()
      .then(() => undefined)
      .finally(() => (this.renewing = null));
    return this.renewing;
  }
}

/** Local development: always signed in, since Netlify Identity only runs on a deployed site. */
export class LocalAuth implements AuthService {
  private readonly account: Account = { email: 'dev@localhost' };

  async current(): Promise<Account | null> {
    return this.account;
  }

  async signIn(): Promise<Account> {
    return this.account;
  }

  async signOut(): Promise<void> {}

  async requestReset(): Promise<void> {}

  async callback(): Promise<Callback | null> {
    return null;
  }

  async acceptInvite(): Promise<Account> {
    return this.account;
  }

  async setPassword(): Promise<void> {}

  async renew(): Promise<void> {}
}
